import React, { useState, useEffect } from 'react';
import { cn } from '../../utils';
import { Button } from '../ui/button';
import { Input, Select } from '../ui/form';
import { useDivisionWorkflows, useDivisionWorkflow, useUpdateDivisionWorkflow } from '../../hooks/use-division-workflows';
import { useModalA11y } from '../../hooks/use-modal-a11y';
import type { DiscoveredField, RoutingRuleDto } from '../../types';

const OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater-than', label: 'Greater Than' },
  { value: 'less-than', label: 'Less Than' },
  { value: 'between', label: 'Between' },
];

interface AddRoutingRuleToWorkflowModalProps {
  isOpen: boolean;
  field: DiscoveredField;
  divisionId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AddRoutingRuleToWorkflowModal({
  isOpen,
  field,
  divisionId,
  onClose,
  onSuccess,
}: AddRoutingRuleToWorkflowModalProps) {
  const [workflowId, setWorkflowId] = useState('');
  const [operator, setOperator] = useState('equals');
  const [value, setValue] = useState(String(field.currentValue ?? ''));
  const [valueEnd, setValueEnd] = useState('');

  // Reset state when field changes
  useEffect(() => {
    setValue(String(field.currentValue ?? ''));
    setValueEnd('');
    setOperator('equals');
  }, [field]);

  const { data: workflowsData, isLoading: workflowsLoading } = useDivisionWorkflows(
    divisionId,
    { active: true },
    !!divisionId
  );

  const { data: selectedWorkflow, isLoading: workflowDetailLoading } = useDivisionWorkflow(
    divisionId,
    workflowId,
    !!workflowId
  );

  const updateMutation = useUpdateDivisionWorkflow(divisionId);

  const workflows = workflowsData?.items || [];

  const handleSave = async () => {
    if (!workflowId || !value) return;
    if (!selectedWorkflow) return;

    const newRule: RoutingRuleDto = {
      id: crypto.randomUUID(),
      field: field.filterBasedJsonPath || field.jsonPath,
      operator,
      value,
      ...(operator === 'between' && valueEnd ? { valueEnd } : {}),
    };

    const existingRules: RoutingRuleDto[] = selectedWorkflow.routingRules || [];

    try {
      await updateMutation.mutateAsync({
        workflowId,
        request: {
          name: selectedWorkflow.name,
          description: selectedWorkflow.description,
          active: selectedWorkflow.active,
          matchingMode: 'routingRules',
          matchCriteria: selectedWorkflow.matchCriteria,
          workflowSteps: selectedWorkflow.steps?.map((step: any) => ({
            step: step.step,
            name: step.name,
            description: step.description,
            stepType: step.stepType || 'approval',
            approvers: step.approvers || { type: 'match_identifier_based', matchIdentifiers: [], parallelApproval: false, requiredApprovals: 1 },
            conditions: step.conditions || { amountThreshold: 0, skipIfApprovedBy: [] },
            canEditQuantity: step.canEditQuantity ?? false,
            orderLevelOnly: step.orderLevelOnly ?? false,
          })) || [],
          routingRules: [...existingRules, newRule],
          settings: selectedWorkflow.settings,
        },
      });

      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Failed to add routing rule to workflow:', err);
    }
  };

  const { dialogRef } = useModalA11y<HTMLDivElement>(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-routing-rule-modal-title"
        tabIndex={-1}
        className="bg-white dark:bg-secondary-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto focus:outline-none"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-route text-purple-600 dark:text-purple-400" />
            <h3 id="add-routing-rule-modal-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Add Routing Rule to Existing Workflow
            </h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-200 transition-colors"
          >
            <i className="fa-solid fa-times" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Field Info */}
          <div className="bg-purple-50 dark:bg-purple-950 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-purple-900 mb-2">
              <i className="fa-solid fa-info-circle mr-2" />
              Discovered Field Details
            </h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-purple-700 dark:text-purple-400 font-medium">Field Name:</span>
                <div className="text-purple-900 font-mono mt-1">{field.fieldName}</div>
              </div>
              <div>
                <span className="text-purple-700 dark:text-purple-400 font-medium">Category:</span>
                <div className="text-purple-900 mt-1">{field.category || 'Unknown'}</div>
              </div>
              <div className="col-span-2">
                <span className="text-purple-700 dark:text-purple-400 font-medium">JSON Path:</span>
                <code className="block text-purple-900 font-mono text-xs mt-1 bg-white dark:bg-secondary-800 rounded px-2 py-1">
                  {field.filterBasedJsonPath || field.jsonPath}
                </code>
              </div>
              {field.currentValue && (
                <div className="col-span-2">
                  <span className="text-purple-700 dark:text-purple-400 font-medium">Current Value:</span>
                  <div className="text-purple-900 mt-1 font-mono text-sm">{field.currentValue}</div>
                </div>
              )}
            </div>
          </div>

          {/* Workflow Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Target Workflow <span className="text-red-500">*</span>
            </label>
            <Select
              value={workflowId}
              onChange={(e) => setWorkflowId(e.target.value)}
              disabled={workflowsLoading}
            >
              <option value="">Select a workflow...</option>
              {workflows.map((workflow) => (
                <option key={workflow.id} value={workflow.id}>
                  {workflow.name}
                  {workflow.matchingMode === 'routingRules' ? ' (routing rules)' : ''}
                </option>
              ))}
            </Select>
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              The routing rule will be added to this workflow. The workflow's matching mode will be set to "Routing Rules".
            </p>
          </div>

          {/* Existing rules hint */}
          {selectedWorkflow && selectedWorkflow.routingRules && selectedWorkflow.routingRules.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
              <p className="text-xs text-yellow-800 font-medium mb-1">
                <i className="fa-solid fa-triangle-exclamation mr-1" />
                Existing Routing Rules ({selectedWorkflow.routingRules.length})
              </p>
              <div className="space-y-1">
                {selectedWorkflow.routingRules.map((rule: RoutingRuleDto, idx: number) => (
                  <div key={idx} className="text-xs text-yellow-700 dark:text-yellow-400 font-mono">
                    {rule.field} <span className="font-semibold">{rule.operator}</span> {rule.value}
                    {rule.valueEnd ? ` ↔ ${rule.valueEnd}` : ''}
                  </div>
                ))}
              </div>
              <p className="text-xs text-yellow-600 mt-2">The new rule will be added alongside these existing rules.</p>
            </div>
          )}

          {/* Operator */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              Operator <span className="text-red-500">*</span>
            </label>
            <Select
              value={operator}
              onChange={(e) => setOperator(e.target.value)}
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>{op.label}</option>
              ))}
            </Select>
          </div>

          {/* Value */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
              {operator === 'between' ? 'From Value' : 'Value'} <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g., Electronics"
            />
            <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
              The value to match against the field
            </p>
          </div>

          {/* ValueEnd for 'between' operator */}
          {operator === 'between' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-secondary-200 mb-2">
                To Value <span className="text-red-500">*</span>
              </label>
              <Input
                type="text"
                value={valueEnd}
                onChange={(e) => setValueEnd(e.target.value)}
                placeholder="e.g., 10000"
              />
              <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                The upper bound for the range
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-secondary-700 bg-gray-50 dark:bg-secondary-900">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={
              !workflowId ||
              !value ||
              (operator === 'between' && !valueEnd) ||
              workflowDetailLoading ||
              updateMutation.isPending
            }
          >
            {updateMutation.isPending ? (
              <>
                <i className="fa-solid fa-spinner fa-spin mr-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa-solid fa-route mr-2" />
                Add Routing Rule
              </>
            )}
          </Button>
        </div>

        {/* Error Display */}
        {updateMutation.isError && (
          <div className="mx-4 mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
              <i className="fa-solid fa-exclamation-circle" />
              <span className="text-sm">
                Failed to add routing rule. Please try again.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
